// src/components/Spreadsheet/GroupedSpreadsheetCanvas.jsx

import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';

import GroupedSpreadsheetSheet from './GroupedSpreadsheetSheet';

import {
  DEFAULT_ROW_APPEND_COUNT,
  DEFAULT_VISIBLE_ROW_COUNT,
  buildDefaultColumnWidthsForGroups,
  buildGeneratedRowId,
  buildGroupRowsSnapshot,
  buildSpreadsheetDiagnostics,
  buildSpreadsheetNavigationIndex,
  clampNumber,
  clearDirtyState,
  createBlankRow,
  createEmptyDirtyState,
  getNextSortDirection,
  getColumnKey,
  getColumnWidth,
  getColumnWidthKey,
  getDirtyGroupIdsArray,
  getGroupId,
  getPasteTargetColumns,
  isSpreadsheetColumnSortable,
  getRowId,
  getVisibleColumns,
  markCellDirty,
  markGroupDirty,
  normalizeColumnWidths,
  normalizePastedValue,
  normalizeSpreadsheetGroup,
  normalizeSpreadsheetRow,
  normalizeString,
  padRowsToVisibleCount,
  parseClipboardTextToGrid,
  resolveNextCellAddress,
  sortSpreadsheetRowsForColumn,
  toArraySafe,
} from './groupedSpreadsheetUtils';

/**
 * GroupedSpreadsheetCanvas
 * -----------------------------------------------------------------------------
 * Shared grouped spreadsheet engine controller.
 *
 * PURPOSE
 *  • Provide a domain-free spreadsheet controller for Form Lists and SmartLists
 *  • Render one unified spreadsheet surface with grouped column blocks
 *  • Keep hot cell edits local to the canvas/cell layer
 *  • Track dirty groups / rows / cells sparsely
 *  • Expose snapshot APIs for adapter-level save/apply logic
 *  • Support spreadsheet-style keyboard, paste, resize, autofit, and row growth
 *
 * ENTERPRISE SPREADSHEET FOCUS MODEL
 *  • The canvas owns keyboard focus in view mode.
 *  • Display cells are visual/selectable surfaces, not individually focused
 *    keyboard targets.
 *  • Editing mode is the only time an inner input/select receives focus.
 *  • Selection lives in canvas state, not DOM focus.
 *
 * WHY THIS MATTERS
 *  • Browser focus on many individual cells can fight scroll position.
 *  • A canvas-focused model prevents scroll snapback caused by the browser
 *    trying to preserve a focused child cell while the user scrolls.
 *  • This is also the correct foundation for row virtualization later because
 *    rows/cells can unmount while selection remains stable in data state.
 *
 * UNIFIED SHEET MODEL
 *  • The canvas renders ONE GroupedSpreadsheetSheet.
 *  • The sheet renders ONE row-number rail.
 *  • Each group contributes a grouped column block.
 *  • Navigation treats each visual row as one flat cell sequence:
 *      Group 1 Option → Group 1 Hint → Group 1 Dep →
 *      Group 2 Option → Group 2 Hint → Group 2 Dep → ...
 *
 * VIRTUALIZATION-READY DESIGN
 *  • Data lives in local canvas state, not mounted DOM nodes
 *  • Cell identity uses groupId + rowId + columnKey
 *  • Snapshot is built from local data state, not visible DOM
 *  • Focus registration is independent from DOM index
 *  • Rows can later be windowed/virtualized without changing domain adapters
 *
 * SCROLL MODEL
 *  • Manual wheel/trackpad/touch scrolling cancels pending programmatic scroll
 *    frames.
 *  • Keyboard navigation may intentionally bring the next selected cell into
 *    view with nearest-edge behavior.
 *  • Normal mouse selection does not programmatically scroll.
 *
 * IMPORTANT
 *  • This component does NOT know about:
 *      - fieldsMetadata
 *      - dropdownOptions
 *      - dropdownDependencies
 *      - managedFields
 *      - managedOptionSets
 *      - Firestore
 *      - save/publish
 * -----------------------------------------------------------------------------
 */

const DEFAULT_ROW_HEIGHT = 24;
const DEFAULT_ROW_NUMBER_WIDTH = 40;
const DEFAULT_GROUP_HEADER_HEIGHT = 32;
const DEFAULT_COLUMN_HEADER_HEIGHT = 26;

const GOOGLE_SHEETS_FOOTER_HEIGHT = 44;
const GOOGLE_SHEETS_ADD_BLUE = '#1a73e8';
const GOOGLE_SHEETS_FOOTER_BG = '#f1f3f4';
const GOOGLE_SHEETS_FOOTER_BORDER = '#dadce0';
const GOOGLE_SHEETS_TEXT = '#3c4043';

/**
 * TEMP DEBUG: column-width resize tracing
 * ---------------------------------------------------------------------------
 * Enable only while diagnosing resize snapback.
 *
 * WHAT THIS TELLS US
 *  • whether the Canvas remounted/reset
 *  • whether reconciliation overwrote a user-resized key
 *  • whether normal resize wrote the expected scoped width
 *  • whether autofit fired unexpectedly
 *
 * Remove these logs or set DEBUG_COLUMN_WIDTHS to false after diagnosis.
 */
const DEBUG_COLUMN_WIDTHS = false;

const summarizeChangedWidthKeys = (prev = {}, next = {}) => {
  const keys = new Set([
    ...Object.keys(prev || {}),
    ...Object.keys(next || {}),
  ]);

  return Array.from(keys)
    .filter((key) => prev?.[key] !== next?.[key])
    .map((key) => ({
      key,
      before: prev?.[key],
      after: next?.[key],
    }));
};

const getColumnWidthDebugState = ({
  widths = {},
  columnWidthKey,
  columnKey,
} = {}) => ({
  scopedWidth: widths?.[columnWidthKey],
  plainWidth: widths?.[columnKey],
  widthKeyCount: Object.keys(widths || {}).length,
});

const logColumnWidthsDebug = (label, payload = {}) => {
  if (!DEBUG_COLUMN_WIDTHS) return;

  console.log(`[GroupedSpreadsheetCanvas:columnWidths] ${label}`, payload);
};

const makeCellRefKey = ({ groupId, rowId, columnKey }) => (
  `${groupId}::${rowId}::${columnKey}`
);

const isPrintableKey = (event) => {
  if (!event) return false;
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.key.length !== 1) return false;
  return true;
};

const isEditableDomTarget = (target) => {
  if (!target || !target.tagName) return false;

  const tagName = String(target.tagName || '').toLowerCase();

  if (tagName === 'input') return true;
  if (tagName === 'textarea') return true;
  if (tagName === 'select') return true;

  return target.isContentEditable === true;
};

const focusElementWithoutScroll = (element) => {
  if (!element || typeof element.focus !== 'function') return;

  try {
    element.focus({ preventScroll: true });
  } catch (error) {
    // Older browsers may not support the focus options object.
    // Fallback keeps the grid usable without coupling the shared
    // engine to browser capability checks.
    element.focus();
  }
};

/**
 * isElementVisibleWithinScrollViewport
 * -----------------------------------------------------------------------------
 * Checks whether a target cell is already visible inside the scroll container's
 * usable spreadsheet body viewport.
 *
 * WHY THIS EXISTS
 *  • Keyboard navigation may call ensureCellVisible(...) many times per second.
 *  • Calling element.scrollIntoView(...) for every Arrow / Enter / Tab movement
 *    can force browser layout work even when the next cell is already visible.
 *  • A spreadsheet should only programmatically scroll when the active cell
 *    leaves the usable viewport.
 *
 * FROZEN-PANE AWARENESS
 *  • The row-number rail is sticky on the left.
 *  • The group header and column header rows are sticky at the top.
 *  • A cell hidden underneath those frozen panes is not truly visible to the
 *    user, even if it is technically inside the container rect.
 *
 * ENTERPRISE RULE
 *  • This helper is domain-free.
 *  • It does not know about Form Lists or SmartLists.
 *  • It does not read/write context, storage, or Firestore.
 *  • It is compatible with future row virtualization because it only inspects
 *    the mounted active cell before deciding whether to scroll.
 */
const isElementVisibleWithinScrollViewport = ({
  container,
  element,
  rowNumberWidth = DEFAULT_ROW_NUMBER_WIDTH,
  groupHeaderHeight = DEFAULT_GROUP_HEADER_HEIGHT,
  columnHeaderHeight = DEFAULT_COLUMN_HEADER_HEIGHT,
  bufferPx = 2,
} = {}) => {
  if (!container || !element) return false;

  if (
    typeof container.getBoundingClientRect !== 'function' ||
    typeof element.getBoundingClientRect !== 'function'
  ) {
    return false;
  }

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  const safeRowNumberWidth = Number.isFinite(Number(rowNumberWidth))
    ? Number(rowNumberWidth)
    : 0;

  const safeHeaderHeight =
    (Number.isFinite(Number(groupHeaderHeight))
      ? Number(groupHeaderHeight)
      : 0) +
    (Number.isFinite(Number(columnHeaderHeight))
      ? Number(columnHeaderHeight)
      : 0);

  const safeBufferPx = Number.isFinite(Number(bufferPx))
    ? Math.max(Number(bufferPx), 0)
    : 0;

  const visibleLeft =
    containerRect.left + safeRowNumberWidth + safeBufferPx;
  const visibleTop =
    containerRect.top + safeHeaderHeight + safeBufferPx;
  const visibleRight = containerRect.right - safeBufferPx;
  const visibleBottom = containerRect.bottom - safeBufferPx;

  if (visibleRight <= visibleLeft || visibleBottom <= visibleTop) {
    return false;
  }

  const visibleWidth = visibleRight - visibleLeft;
  const visibleHeight = visibleBottom - visibleTop;

  const horizontallyVisible =
    elementRect.width > visibleWidth
      ? elementRect.left <= visibleLeft && elementRect.right >= visibleRight
      : elementRect.left >= visibleLeft && elementRect.right <= visibleRight;

  const verticallyVisible =
    elementRect.height > visibleHeight
      ? elementRect.top <= visibleTop && elementRect.bottom >= visibleBottom
      : elementRect.top >= visibleTop && elementRect.bottom <= visibleBottom;

  return horizontallyVisible && verticallyVisible;
};

/**
 * isValidColumnResizeWidth
 * -----------------------------------------------------------------------------
 * Guards resize math from JavaScript coercion edge cases.
 *
 * WHY
 *  • Number(null) === 0, so Number.isFinite(Number(null)) is true.
 *  • During resize, pendingColumnResizeWidthRef.current can legitimately be
 *    null after the RAF flush has consumed it.
 *  • Treating null as a valid final width causes clampNumber(null, min, max)
 *    to snap the column to its minWidth.
 *
 * ENTERPRISE RULE
 *  • Only explicit numeric width values are valid resize values.
 *  • null / undefined / empty string are lifecycle markers, not widths.
 */
const isValidColumnResizeWidth = (value) => {
  if (value == null) return false;

  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }

  return Number.isFinite(Number(value));
};

/* -------------------------------------------------------------------------- */
/* Group normalization                                                        */
/* -------------------------------------------------------------------------- */

const normalizeGroupsForCanvas = ({
  groups = [],
  columns = [],
  groupIdKey = 'groupId',
  rowIdKey = 'rowId',
  rowKey = 'rows',
  visibleRowCount = DEFAULT_VISIBLE_ROW_COUNT,
  generatedPrefix = 'sheet-row',
} = {}) => {
  return toArraySafe(groups).map((group) => {
    const groupColumns =
      Array.isArray(group?.columns) && group.columns.length > 0
        ? group.columns
        : columns;

    return normalizeSpreadsheetGroup({
      group,
      columns: groupColumns,
      groupIdKey,
      rowIdKey,
      rowKey,
      visibleRowCount,
      generatedPrefix,
    });
  });
};

const buildGroupIndex = (groups = []) => {
  return toArraySafe(groups).reduce((acc, group) => {
    const groupId = getGroupId(group);
    if (!groupId) return acc;

    acc[groupId] = group;
    return acc;
  }, {});
};

/**
 * buildCanvasGroupsSyncSignature
 * -----------------------------------------------------------------------------
 * Builds a stable comparison signature for normalized group data.
 *
 * WHY
 *  • Parent contexts may produce new object references on render.
 *  • The canvas should not replace localGroups unless the incoming spreadsheet
 *    data has meaningfully changed.
 *  • Avoiding unnecessary replacement prevents visual jumpiness and protects
 *    scroll position.
 *
 * IMPORTANT
 *  • This is intentionally a data signature, not a deep clone.
 *  • It includes row ids and visible column values.
 *  • It is domain-free and works for Form Lists and SmartLists.
 */
const buildCanvasGroupsSyncSignature = ({
  groups = [],
  rowKey = 'rows',
  rowIdKey = 'rowId',
} = {}) => {
  return toArraySafe(groups).map((group) => {
    const groupId = getGroupId(group);
    const groupColumns = getVisibleColumns(group?.columns || []);
    const columnKeys = groupColumns
      .map((column) => getColumnKey(column))
      .filter(Boolean);

    const rowSignature = toArraySafe(group?.[rowKey]).map((row) => {
      const rowId = getRowId(row, rowIdKey);

      const valueSignature = columnKeys.map((columnKey) => {
        const value = row?.[columnKey];
        return `${columnKey}:${value == null ? '' : String(value)}`;
      }).join('|');

      return `${rowId}{${valueSignature}}`;
    }).join(';');

    return `${groupId}[${columnKeys.join(',')}](${rowSignature})`;
  }).join('||');
};

/* -------------------------------------------------------------------------- */
/* Width helpers                                                              */
/* -------------------------------------------------------------------------- */

const getGroupRenderedWidth = ({
  group,
  columns = [],
  columnWidths = {},
} = {}) => {
  const groupId = getGroupId(group);

  const groupColumns =
    Array.isArray(group?.columns) && group.columns.length > 0
      ? group.columns
      : columns;

  const visibleColumns = getVisibleColumns(groupColumns);

  return visibleColumns.reduce((sum, column) => {
    return sum + getColumnWidth({
      groupId,
      column,
      columnWidths,
      fallback: 100,
    });
  }, 0);
};

const getCanvasContentWidth = ({
  groups = [],
  columns = [],
  columnWidths = {},
  rowNumberWidth = DEFAULT_ROW_NUMBER_WIDTH,
} = {}) => {
  const groupedContentWidth = toArraySafe(groups).reduce((sum, group) => {
    return sum + getGroupRenderedWidth({
      group,
      columns,
      columnWidths,
    });
  }, 0);

  // IMPORTANT:
  // Row numbers are a single sticky sheet lane, not one lane per group.
  // This matches the SmartLists spreadsheet structure and prevents each
  // group from looking like a separate mini-spreadsheet.
  return rowNumberWidth + groupedContentWidth;
};

/* -------------------------------------------------------------------------- */
/* Cell descriptors and write guards                                          */
/* -------------------------------------------------------------------------- */

const getGroupColumns = ({
  group,
  columns,
}) => {
  return Array.isArray(group?.columns) && group.columns.length > 0
    ? group.columns
    : columns;
};

const findCellDescriptor = ({
  groups = [],
  columns = [],
  cellAddress = null,
  rowKey = 'rows',
  rowIdKey = 'rowId',
} = {}) => {
  if (!cellAddress) return null;

  const targetGroupId = getGroupId({
    groupId: cellAddress.groupId,
  });

  const targetRowId = String(cellAddress.rowId || '');
  const targetColumnKey = String(cellAddress.columnKey || '');

  if (!targetGroupId || !targetRowId || !targetColumnKey) {
    return null;
  }

  for (let groupIndex = 0; groupIndex < toArraySafe(groups).length; groupIndex += 1) {
    const group = groups[groupIndex];
    const groupId = getGroupId(group);

    if (groupId !== targetGroupId) {
      continue;
    }

    const rows = toArraySafe(group?.[rowKey]);
    const rowIndex = rows.findIndex(
      (row) => getRowId(row, rowIdKey) === targetRowId,
    );

    if (rowIndex < 0) {
      return null;
    }

    const row = rows[rowIndex];
    const groupColumns = getGroupColumns({
      group,
      columns,
    });

    const visibleColumns = getVisibleColumns(groupColumns);
    const columnIndex = visibleColumns.findIndex(
      (column) => getColumnKey(column) === targetColumnKey,
    );

    if (columnIndex < 0) {
      return null;
    }

    const column = visibleColumns[columnIndex];

    return {
      group,
      groupId,
      groupIndex,
      row,
      rowId: targetRowId,
      rowIndex,
      column,
      columnKey: targetColumnKey,
      columnIndex,
      columns: visibleColumns,
    };
  }

  return null;
};

const isCellBlocked = ({
  readOnly,
  disabled,
  group,
  row,
  column,
  groupId,
  rowId,
  rowIndex,
  columnKey,
  columnIndex,
  isCellReadOnly,
  isCellDisabled,
}) => {
  if (readOnly || disabled) return true;
  if (group?.readOnly === true || group?.disabled === true) return true;
  if (row?.readOnly === true || row?.disabled === true) return true;
  if (column?.readOnly === true || column?.disabled === true) return true;

  if (typeof isCellReadOnly === 'function') {
    const blocked = isCellReadOnly({
      groupId,
      group,
      row,
      rowId,
      rowIndex,
      column,
      columnKey,
      columnIndex,
    }) === true;

    if (blocked) return true;
  }

  if (typeof isCellDisabled === 'function') {
    return isCellDisabled({
      groupId,
      group,
      row,
      rowId,
      rowIndex,
      column,
      columnKey,
      columnIndex,
    }) === true;
  }

  return false;
};

const canEditCell = ({
  descriptor,
  readOnly,
  disabled,
  isCellReadOnly,
  isCellDisabled,
}) => {
  if (!descriptor) return false;

  const {
    group,
    row,
    rowId,
    rowIndex,
    column,
    columnKey,
    columnIndex,
    groupId,
  } = descriptor;

  if (column?.type === 'custom') return false;

  return !isCellBlocked({
    readOnly,
    disabled,
    group,
    row,
    rowId,
    rowIndex,
    column,
    columnKey,
    columnIndex,
    groupId,
    isCellReadOnly,
    isCellDisabled,
  });
};

/**
 * getCellComparableValue
 * -----------------------------------------------------------------------------
 * Builds a stable display-equivalent comparison value for canvas-level actions.
 *
 * WHY
 *  • Cell editing normalizes values before commit inside GroupedSpreadsheetCell.
 *  • Canvas-level actions like Delete/Backspace bypass the edit input.
 *  • We need a lightweight way to avoid marking an already-empty cell dirty.
 */
const getCellComparableValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';

  return String(value);
};

/**
 * resolveClearedCellValue
 * -----------------------------------------------------------------------------
 * Determines the value written when a selected cell is cleared with Delete or
 * Backspace.
 *
 * DEFAULT BEHAVIOR
 *  • Google-Sheets-like clear means the cell becomes an empty string.
 *
 * EXTENSION POINTS
 *  • column.clearValue can provide a custom empty value.
 *  • column.getClearedValue(row, column) can compute a custom empty value.
 *  • column.normalizeCommittedValue(...) is respected when present so column
 *    adapters can keep their existing value normalization rules.
 *
 * IMPORTANT
 *  • This is domain-free.
 *  • The canvas does not know whether the cell belongs to Form Lists,
 *    SmartLists, or any future spreadsheet consumer.
 */
const resolveClearedCellValue = ({
  column,
  row,
} = {}) => {
  let clearedValue = '';

  if (typeof column?.getClearedValue === 'function') {
    clearedValue = column.getClearedValue(row, column);
  } else if (Object.prototype.hasOwnProperty.call(column || {}, 'clearValue')) {
    clearedValue = column.clearValue;
  }

  if (typeof column?.normalizeCommittedValue === 'function') {
    return column.normalizeCommittedValue(clearedValue, row, column);
  }

  return clearedValue;
};

const cloneRowsWithCapacity = ({
  rows = [],
  targetLength,
  columns,
  groupId,
  rowIdKey,
  generatedPrefix,
}) => {
  const nextRows = [...rows];

  for (let rowIndex = nextRows.length; rowIndex < targetLength; rowIndex += 1) {
    const blankRow = createBlankRow({
      columns,
      rowId: buildGeneratedRowId({
        groupId,
        rowIndex,
        prefix: generatedPrefix,
      }),
      rowIdKey,
    });

    nextRows.push(
      normalizeSpreadsheetRow({
        row: blankRow,
        columns,
        groupId,
        rowIndex,
        rowIdKey,
        generatedPrefix,
      }),
    );
  }

  return nextRows;
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const GroupedSpreadsheetCanvas = memo(forwardRef(({
  groups = [],
  columns = [],
  initialColumnWidths = null,
  visibleRowCount = DEFAULT_VISIBLE_ROW_COUNT,
  rowAppendCount = DEFAULT_ROW_APPEND_COUNT,
  rowHeight = DEFAULT_ROW_HEIGHT,
  rowNumberWidth = DEFAULT_ROW_NUMBER_WIDTH,
  groupHeaderHeight = DEFAULT_GROUP_HEADER_HEIGHT,
  columnHeaderHeight = DEFAULT_COLUMN_HEADER_HEIGHT,
  groupIdKey = 'groupId',
  rowIdKey = 'rowId',
  rowKey = 'rows',
  generatedRowPrefix = 'sheet-row',
  readOnly = false,
  disabled = false,
  sx = null,
  onDirtyStateChange,
  includeDirtyDiagnostics = true,
  showDirtyCellIndicator = true,
  onSelectionChange,
  onColumnWidthsChange,
  onCellCommit,
  onCommitBoundary,

  /**
   * Optional domain adapter hook.
   *
   * The shared Spreadsheet/ engine stays domain-free. Consumers such as
   * Form Lists may normalize a committed row after a cell edit/paste, for
   * example by assigning a stable optionId when a row becomes meaningful.
   *
   * IMPORTANT
   *  • The canvas does not know what the normalizer does.
   *  • The canvas does not import Form Lists logic.
   *  • If omitted, rows are committed exactly as normal.
   */
  normalizeCommittedRow,

  renderCustomCell,
  renderGroupHeaderContent,
  renderGroupActions,
  isCellReadOnly,
  isCellDisabled,
  isCellDirty,
  isColumnSortDisabled,
}, ref) => {
  /**
   * Visible row count control
   * ---------------------------------------------------------------------------
   * This is the shared-engine source of truth for how many rows the canvas
   * visibly renders.
   *
   * WHY THIS EXISTS
   *  • The spreadsheet should always render a useful baseline of rows.
   *  • Users can explicitly add more rows from the footer UI.
   *  • Incoming real data may already exceed the current requested row count.
   *
   * RULES
   *  • never render fewer than the visibleRowCount prop
   *  • never render fewer than the largest incoming group row count
   *  • when the footer requests more rows, increase the visible baseline
   *
   * IMPORTANT
   *  • This does NOT mean blank rows are persisted.
   *  • Outgoing snapshots still drop padded blank rows.
   * ---------------------------------------------------------------------------
   */
  const [requestedVisibleRowCount, setRequestedVisibleRowCount] = useState(
    () => Math.max(Number(visibleRowCount) || DEFAULT_VISIBLE_ROW_COUNT, 1),
  );

  const [rowsToAddInput, setRowsToAddInput] = useState(
    () => String(rowAppendCount || DEFAULT_ROW_APPEND_COUNT),
  );

  const [isAddHovered, setIsAddHovered] = useState(false);

  const normalizedInitialGroups = useMemo(() => (
    normalizeGroupsForCanvas({
      groups,
      columns,
      groupIdKey,
      rowIdKey,
      rowKey,
      visibleRowCount: requestedVisibleRowCount,
      generatedPrefix: generatedRowPrefix,
    })
  ), [
    groups,
    columns,
    groupIdKey,
    rowIdKey,
    rowKey,
    requestedVisibleRowCount,
    generatedRowPrefix,
  ]);

  const [localGroups, setLocalGroups] = useState(normalizedInitialGroups);
  const localGroupsRef = useRef(normalizedInitialGroups);

  const [dirtyState, setDirtyState] = useState(() => createEmptyDirtyState());
  const dirtyStateRef = useRef(dirtyState);

  const [selectedCell, setSelectedCell] = useState(null);
  const selectedCellRef = useRef(null);

  const [editingCell, setEditingCell] = useState(null);
  const editingCellRef = useRef(null);

  /**
   * Local sort state
   * ---------------------------------------------------------------------------
   * Sorting is a local spreadsheet action.
   *
   * WHY THIS LIVES IN THE CANVAS
   *  • The shared Spreadsheet engine owns local row order while mounted.
   *  • Sorting should reorder local canvas rows immediately without writing
   *    Form Lists context, SmartLists context, storage, or Firestore.
   *  • Adapter-level Save Draft / Publish flows can later persist the sorted
   *    order through the existing dirty-group snapshot boundary.
   *
   * IMPORTANT
   *  • Sort state is visual/workspace state.
   *  • Row identity is preserved by moving whole row objects.
   *  • Dependencies remain safe because Form Lists dependencies key by optionId,
   *    not visual row index.
   */
  const [sortState, setSortState] = useState(null);
  const sortStateRef = useRef(null);

  const initialWidths = useMemo(() => {
    const defaultScopedWidths = buildDefaultColumnWidthsForGroups({
      groups: normalizedInitialGroups,
      columns,
    });

    return normalizeColumnWidths({
      groups: normalizedInitialGroups,
      columns,
      columnWidths: {
        ...defaultScopedWidths,
        ...(initialColumnWidths || {}),
      },
    });
  }, [
    normalizedInitialGroups,
    columns,
    initialColumnWidths,
  ]);

  const [columnWidths, setColumnWidths] = useState(initialWidths);
  const columnWidthsRef = useRef(initialWidths);

  const cellRefsRef = useRef(new Map());
  const scrollContainerRef = useRef(null);
  const scrollFrameRef = useRef(null);
  const columnResizeFrameRef = useRef(null);
  const pendingColumnResizeWidthRef = useRef(null);

  /**
   * Active column resize session
   * ---------------------------------------------------------------------------
   * Tracks the currently owned visual-column resize gesture.
   *
   * WHY
   *  • A local spreadsheet treats mouse-down on a column boundary as ownership
   *    of that exact visual column.
   *  • The current implementation only writes a scoped width after mousemove.
   *  • That means the first resize can briefly depend on fallback/default width
   *    state until movement is committed.
   *  • Double-click autofit works because it creates the scoped width first.
   *
   * ENTERPRISE RULE
   *  • Normal drag must also create the scoped width immediately on resize start.
   *  • Mouseup should commit the latest known session width, not only the
   *    pending animation-frame value.
   */
  const columnResizeSessionRef = useRef(null);

  /**
   * Column resize lifecycle guards
   * ---------------------------------------------------------------------------
   * WHY
   *  • Column resizing is a live pointer-driven interaction.
   *  • Layout/context sync effects can legitimately run while a user is dragging.
   *  • Those effects must not reconcile widths back toward defaults mid-drag.
   *
   * ENTERPRISE RULE
   *  • During active resize, the resize gesture owns column width state.
   *  • Layout/default reconciliation is deferred until the gesture ends.
   *  • This keeps Google-Sheets-like resizing immediate and prevents snapback.
   */
  const isColumnResizeActiveRef = useRef(false);
  const pendingColumnWidthSyncRef = useRef(false);

  /**
   * Printable-key typeahead buffer
   * ---------------------------------------------------------------------------
   * A fast spreadsheet user can press Enter and immediately type the next value
   * before the next cell's edit input has mounted and focused.
   *
   * Without a buffer, the canvas receives multiple printable keydown events and
   * repeatedly calls beginEditingCell(..., event.key), which can overwrite the
   * intended initial value and drop characters.
   *
   * This buffer keeps those printable keys in the canvas until the controlled
   * editing cell has received the full initial value.
   *
   * ENTERPRISE HANDOFF RULE
   *  • Do not clear this buffer using animation-frame timing.
   *  • Clear it only when the target edit input confirms it is mounted/focused.
   *  • This prevents rapid typing after Enter from being split into partial
   *    buffers like "f" and "ve" before MUI/React focus has completed.
   *
   * IMPORTANT
   *  • This is not domain-specific.
   *  • This does not write context/storage.
   *  • This is part of the shared spreadsheet keyboard model.
   */
  const typeaheadBufferRef = useRef('');
  const typeaheadCellKeyRef = useRef('');

  /**
   * Memoized keyboard navigation index
   * ---------------------------------------------------------------------------
   * Keyboard navigation should not rebuild a flat row/cell model on every
   * Arrow / Enter / Tab event.
   *
   * WHY
   *  • Large grouped sheets can contain many field groups and visible columns.
   *  • The old resolveNextCellAddress(...) path derived row cells during each
   *    keydown.
   *  • This index is rebuilt only when localGroups/columns change.
   *
   * IMPORTANT
   *  • This is a shared Spreadsheet-engine optimization.
   *  • It is domain-free and works for Form Lists and SmartLists.
   *  • It does not change selection identity.
   *  • It is compatible with future row virtualization because it is based on
   *    data state, not mounted DOM nodes.
   */
  const navigationIndexRef = useRef(null);

  /* ------------------------------------------------------------------------ */
  /* State update helpers                                                     */
  /* ------------------------------------------------------------------------ */

  const updateLocalGroups = useCallback((producer) => {
    const current = localGroupsRef.current || [];
    const next =
      typeof producer === 'function' ? producer(current) : producer;

    if (next === current) {
      return current;
    }

    localGroupsRef.current = next;
    setLocalGroups(next);

    return next;
  }, []);

  const updateDirtyState = useCallback((producer) => {
    const current = dirtyStateRef.current || createEmptyDirtyState();
    const next =
      typeof producer === 'function' ? producer(current) : producer;

    if (next === current) {
      return current;
    }

    dirtyStateRef.current = next;
    setDirtyState(next);

    return next;
  }, []);

  const updateColumnWidths = useCallback((producer) => {
    const current = columnWidthsRef.current || {};
    const next =
      typeof producer === 'function' ? producer(current) : producer;

    if (next === current) {
      return current;
    }

    columnWidthsRef.current = next;
    setColumnWidths(next);

    return next;
  }, []);

  /**
   * reconcileColumnWidthsForCurrentGroups
   * ---------------------------------------------------------------------------
   * Keeps the column-width map aligned with the currently rendered groups.
   *
   * WHY THIS EXISTS
   *  • New groups need default scoped width keys.
   *  • Removed/reordered groups should not break existing width state.
   *  • Existing user-resized scoped widths must always win over defaults.
   *
   * IMPORTANT
   *  • This must use the latest `prev` from updateColumnWidths(...), not a
   *    normalized object calculated outside the state updater.
   *  • During an active drag-resize, reconciliation is deferred so layout sync
   *    cannot snap the visual column back to its default width.
   */
  const reconcileColumnWidthsForCurrentGroups = useCallback(({
    force = false,
  } = {}) => {
    if (isColumnResizeActiveRef.current && force !== true) {
      logColumnWidthsDebug('reconcile:deferredDuringActiveResize', {
        force,
        activeResizeSession: columnResizeSessionRef.current,
        currentWidthKeys: Object.keys(columnWidthsRef.current || {}),
      });

      pendingColumnWidthSyncRef.current = true;
      return;
    }

    updateColumnWidths((prev) => {
      const widthGroups =
        Array.isArray(localGroupsRef.current) &&
        localGroupsRef.current.length > 0
          ? localGroupsRef.current
          : normalizedInitialGroups;

      const defaultScopedWidths = buildDefaultColumnWidthsForGroups({
        groups: widthGroups,
        columns,
      });

      const normalized = normalizeColumnWidths({
        groups: widthGroups,
        columns,
        columnWidths: {
          ...defaultScopedWidths,
          ...(initialColumnWidths || {}),
          ...(prev || {}),
        },
      });

      const prevSignature = JSON.stringify(prev || {});
      const nextSignature = JSON.stringify(normalized || {});

      if (prevSignature === nextSignature) {
        return prev;
      }

      logColumnWidthsDebug('reconcile:appliedWidthMapChange', {
        force,
        activeResize: isColumnResizeActiveRef.current,
        activeResizeSession: columnResizeSessionRef.current,
        groupCount: widthGroups.length,
        changedWidthKeys: summarizeChangedWidthKeys(prev, normalized),
      });

      return normalized;
    });
  }, [
    columns,
    initialColumnWidths,
    normalizedInitialGroups,
    updateColumnWidths,
  ]);

  const updateSelectedCell = useCallback((nextCell) => {
    selectedCellRef.current = nextCell;
    setSelectedCell(nextCell);
  }, []);

  const updateEditingCell = useCallback((nextEditingCell) => {
    editingCellRef.current = nextEditingCell;
    setEditingCell(nextEditingCell);
  }, []);

  const updateSortState = useCallback((nextSortState) => {
    sortStateRef.current = nextSortState;
    setSortState(nextSortState);
  }, []);

  const navigationIndex = useMemo(() => (
    buildSpreadsheetNavigationIndex({
      groups: localGroups,
      columns,
      rowKey,
      rowIdKey,
    })
  ), [
    localGroups,
    columns,
    rowKey,
    rowIdKey,
  ]);

  useEffect(() => {
    navigationIndexRef.current = navigationIndex;
  }, [navigationIndex]);

  /**
   * emitCommitBoundary
   * ---------------------------------------------------------------------------
   * Domain-free commit signal emitted by the shared spreadsheet engine.
   *
   * WHY
   *  • The canvas should not know whether a commit should update Form Lists,
   *    SmartLists, local storage, Firestore, or nothing.
   *  • Parent adapters/pages can decide what to do with committed changes.
   *
   * IMPORTANT
   *  • This is NOT a server save.
   *  • This is NOT a storage write.
   *  • This is only a clean spreadsheet boundary event:
   *      - one cell committed
   *      - one paste operation committed
   * ---------------------------------------------------------------------------
   */
  const emitCommitBoundary = useCallback((payload = {}) => {
    if (typeof onCommitBoundary !== 'function') {
      return;
    }

    onCommitBoundary({
      ...payload,
      timestamp: Date.now(),
    });
  }, [onCommitBoundary]);

  /* ------------------------------------------------------------------------ */
  /* Canvas focus and scroll coordination                                     */
  /* ------------------------------------------------------------------------ */

  const focusCanvas = useCallback(() => {
    focusElementWithoutScroll(scrollContainerRef.current);
  }, []);

  const cancelPendingScrollFrame = useCallback(() => {
    if (scrollFrameRef.current) {
      window.cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    logColumnWidthsDebug('canvas:mount', {
      initialWidthKeys: Object.keys(columnWidthsRef.current || {}),
      initialWidths: columnWidthsRef.current,
      localGroupCount: Array.isArray(localGroupsRef.current)
        ? localGroupsRef.current.length
        : 0,
    });

    return () => {
      logColumnWidthsDebug('canvas:unmount', {
        latestWidthKeys: Object.keys(columnWidthsRef.current || {}),
        latestWidths: columnWidthsRef.current,
        activeResizeSession: columnResizeSessionRef.current,
      });

      if (scrollFrameRef.current) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }

      if (columnResizeFrameRef.current) {
        window.cancelAnimationFrame(columnResizeFrameRef.current);
        columnResizeFrameRef.current = null;
      }

      pendingColumnResizeWidthRef.current = null;
      columnResizeSessionRef.current = null;
      isColumnResizeActiveRef.current = false;
      pendingColumnWidthSyncRef.current = false;
    };
  }, []);

  const ensureCellVisible = useCallback((cellAddress) => {
    if (!cellAddress) return;

    cancelPendingScrollFrame();

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;

      const container = scrollContainerRef.current;
      const key = makeCellRefKey(cellAddress);
      const refRecord = cellRefsRef.current.get(key);
      const element = refRecord?.element;

      if (!container || !element || typeof element.scrollIntoView !== 'function') {
        return;
      }

      /**
       * Viewport-aware scroll guard
       * -----------------------------------------------------------------------
       * Do not ask the browser to scroll if the next selected cell is already
       * visible inside the usable spreadsheet body viewport.
       *
       * WHY
       *  • scrollIntoView(...) can force layout work.
       *  • Repeated Arrow-key navigation can fire faster than React/browser
       *    paint, which makes selection appear to "stick" and then jump.
       *  • Avoiding unnecessary scroll work keeps the hot keyboard path closer
       *    to local spreadsheet behavior.
       *
       * IMPORTANT
       *  • We still scroll when the cell is outside the visible body viewport.
       *  • The helper accounts for the sticky row-number rail and sticky header
       *    stack so cells hidden under frozen panes are treated as not visible.
       */
      const alreadyVisible = isElementVisibleWithinScrollViewport({
        container,
        element,
        rowNumberWidth,
        groupHeaderHeight,
        columnHeaderHeight,
        bufferPx: 2,
      });

      if (alreadyVisible) {
        return;
      }

      element.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    });
  }, [
    cancelPendingScrollFrame,
    rowNumberWidth,
    groupHeaderHeight,
    columnHeaderHeight,
  ]);

  const handleManualScrollIntent = useCallback(() => {
    cancelPendingScrollFrame();
  }, [cancelPendingScrollFrame]);

  const clearTypeaheadBuffer = useCallback(() => {
    typeaheadBufferRef.current = '';
    typeaheadCellKeyRef.current = '';
  }, []);

  const buildTypeaheadCellKey = useCallback((cellAddress = {}) => {
    return makeCellRefKey({
      groupId: cellAddress.groupId || '',
      rowId: cellAddress.rowId || '',
      columnKey: cellAddress.columnKey || '',
    });
  }, []);

  const appendPrintableKeyToTypeahead = useCallback((cellAddress, key) => {
    if (!cellAddress || typeof key !== 'string' || key.length !== 1) {
      return '';
    }

    const nextCellKey = buildTypeaheadCellKey(cellAddress);

    if (typeaheadCellKeyRef.current !== nextCellKey) {
      typeaheadCellKeyRef.current = nextCellKey;
      typeaheadBufferRef.current = '';
    }

    typeaheadBufferRef.current += key;

    /**
     * Keep the buffer alive until the edit input confirms it is ready.
     *
     * WHY
     *  • A fast spreadsheet user can press Enter and immediately type.
     *  • React/MUI may not mount/focus the next TextField before the next
     *    printable keydown reaches the canvas.
     *  • Clearing this buffer by frame timing can drop or split characters.
     *
     * Once the edit input has mounted/focused, GroupedSpreadsheetCell calls
     * onEditInputReady(...), and the canvas clears the buffer explicitly.
     */
    return typeaheadBufferRef.current;
  }, [
    buildTypeaheadCellKey,
  ]);

  const handleEditInputReady = useCallback((cellAddress = {}) => {
    const readyCellKey = buildTypeaheadCellKey(cellAddress);

    if (
      readyCellKey &&
      readyCellKey === typeaheadCellKeyRef.current
    ) {
      clearTypeaheadBuffer();
    }
  }, [
    buildTypeaheadCellKey,
    clearTypeaheadBuffer,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Visible row count synchronization                                        */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const nextBaseline = Math.max(
      Number(visibleRowCount) || DEFAULT_VISIBLE_ROW_COUNT,
      1,
    );

    setRequestedVisibleRowCount((prev) => Math.max(prev, nextBaseline));
  }, [visibleRowCount]);

  useEffect(() => {
    setRowsToAddInput(String(rowAppendCount || DEFAULT_ROW_APPEND_COUNT));
  }, [rowAppendCount]);

  useEffect(() => {
    const largestIncomingGroup = toArraySafe(groups).reduce((acc, group) => {
      const sourceRows = Array.isArray(group?.[rowKey])
        ? group[rowKey]
        : [];

      return Math.max(acc, sourceRows.length);
    }, 0);

    setRequestedVisibleRowCount((prev) => Math.max(
      prev,
      largestIncomingGroup,
      Number(visibleRowCount) || DEFAULT_VISIBLE_ROW_COUNT,
    ));
  }, [
    groups,
    rowKey,
    visibleRowCount,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Incoming group synchronization                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const dirtyGroupIds = dirtyStateRef.current?.dirtyGroupIds;
    const hasDirtyGroups =
      dirtyGroupIds instanceof Set && dirtyGroupIds.size > 0;

    if (!hasDirtyGroups) {
      updateLocalGroups((prevGroups) => {
        const prevSignature = buildCanvasGroupsSyncSignature({
          groups: prevGroups,
          rowKey,
          rowIdKey,
        });

        const nextSignature = buildCanvasGroupsSyncSignature({
          groups: normalizedInitialGroups,
          rowKey,
          rowIdKey,
        });

        if (prevSignature === nextSignature) {
          return prevGroups;
        }

        return normalizedInitialGroups;
      });

      return;
    }

    updateLocalGroups((prevGroups) => {
      const prevById = buildGroupIndex(prevGroups);

      const nextGroups = normalizedInitialGroups.map((incomingGroup) => {
        const groupId = getGroupId(incomingGroup);

        if (groupId && dirtyGroupIds.has(groupId)) {
          return prevById[groupId] || incomingGroup;
        }

        return incomingGroup;
      });

      const prevSignature = buildCanvasGroupsSyncSignature({
        groups: prevGroups,
        rowKey,
        rowIdKey,
      });

      const nextSignature = buildCanvasGroupsSyncSignature({
        groups: nextGroups,
        rowKey,
        rowIdKey,
      });

      if (prevSignature === nextSignature) {
        return prevGroups;
      }

      return nextGroups;
    });
  }, [
    normalizedInitialGroups,
    rowKey,
    rowIdKey,
    updateLocalGroups,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Column width synchronization                                             */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    reconcileColumnWidthsForCurrentGroups();
  }, [
    reconcileColumnWidthsForCurrentGroups,
  ]);

  /* ------------------------------------------------------------------------ */
  /* External notifications                                                   */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (typeof onDirtyStateChange !== 'function') return;

    /**
     * Dirty-state notification
     * -------------------------------------------------------------------------
     * Most hot-path consumers only need the sparse dirtyState object.
     *
     * Diagnostics are still supported, but they can be expensive because they
     * inspect the current group/row model. Keep them opt-in per consumer so
     * spreadsheet editing does not pay for debug/diagnostic work on every
     * dirty-state change.
     *
     * IMPORTANT
     *  • Default includeDirtyDiagnostics=true preserves existing behavior.
     *  • FormListsLab passes false because it only needs dirty counts.
     *  • This remains domain-free and does not change dirty-state ownership.
     */
    const payload = {
      dirtyState,
    };

    if (includeDirtyDiagnostics) {
      payload.diagnostics = buildSpreadsheetDiagnostics({
        groups: localGroupsRef.current,
        dirtyState,
        rowKey,
      });
    }

    onDirtyStateChange(payload);
  }, [
    dirtyState,
    includeDirtyDiagnostics,
    onDirtyStateChange,
    rowKey,
  ]);

  useEffect(() => {
    if (typeof onSelectionChange !== 'function') return;
    onSelectionChange(selectedCell);
  }, [
    selectedCell,
    onSelectionChange,
  ]);

  useEffect(() => {
    if (typeof onColumnWidthsChange !== 'function') return;
    onColumnWidthsChange(columnWidths);
  }, [
    columnWidths,
    onColumnWidthsChange,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Cell refs                                                                */
  /* ------------------------------------------------------------------------ */

  const registerCellRef = useCallback(({
    groupId,
    rowId,
    columnKey,
    element,
    focus,
    flushPendingEdit,
  }) => {
    const key = makeCellRefKey({ groupId, rowId, columnKey });

    cellRefsRef.current.set(key, {
      element,
      focus,
      flushPendingEdit,
    });

    return () => {
      cellRefsRef.current.delete(key);
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Selection and edit mode                                                  */
  /* ------------------------------------------------------------------------ */

  const handleSelectCell = useCallback((cell) => {
    const nextCell = {
      groupId: cell?.groupId || '',
      rowId: cell?.rowId || '',
      columnKey: cell?.columnKey || '',
    };

    clearTypeaheadBuffer();
    updateSelectedCell(nextCell);
    updateEditingCell(null);
    focusCanvas();
  }, [
    clearTypeaheadBuffer,
    focusCanvas,
    updateEditingCell,
    updateSelectedCell,
  ]);

  const beginEditingCell = useCallback((cellAddress, initialValue = null) => {
    if (initialValue == null) {
      clearTypeaheadBuffer();
    }

    const descriptor = findCellDescriptor({
      groups: localGroupsRef.current,
      columns,
      cellAddress,
      rowKey,
      rowIdKey,
    });

    if (!descriptor) return false;

    if (!canEditCell({
      descriptor,
      readOnly,
      disabled,
      isCellReadOnly,
      isCellDisabled,
    })) {
      return false;
    }

    const nextEditingCell = {
      groupId: descriptor.groupId,
      rowId: descriptor.rowId,
      columnKey: descriptor.columnKey,
      initialValue,
    };

    updateSelectedCell({
      groupId: descriptor.groupId,
      rowId: descriptor.rowId,
      columnKey: descriptor.columnKey,
    });

    updateEditingCell(nextEditingCell);

    return true;
  }, [
    clearTypeaheadBuffer,
    columns,
    disabled,
    isCellDisabled,
    isCellReadOnly,
    readOnly,
    rowIdKey,
    rowKey,
    updateEditingCell,
    updateSelectedCell,
  ]);

  const handleRequestCellEdit = useCallback((cell) => {
    beginEditingCell({
      groupId: cell?.groupId,
      rowId: cell?.rowId,
      columnKey: cell?.columnKey,
    }, cell?.initialValue ?? null);
  }, [beginEditingCell]);

  const handleEndCellEdit = useCallback(() => {
    clearTypeaheadBuffer();
    updateEditingCell(null);
    focusCanvas();
  }, [
    clearTypeaheadBuffer,
    focusCanvas,
    updateEditingCell,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Cell commit                                                              */
  /* ------------------------------------------------------------------------ */

  const handleCommitCellValue = useCallback((payload) => {
    const {
      groupId,
      rowId,
      rowIndex,
      columnKey,
      value,
    } = payload;

    let committedRowForEvent = null;

    updateLocalGroups((prevGroups) => (
      prevGroups.map((group) => {
        if (getGroupId(group) !== groupId) {
          return group;
        }

        const groupColumns =
          Array.isArray(group?.columns) && group.columns.length > 0
            ? group.columns
            : columns;

        const rows = toArraySafe(group?.[rowKey]);
        const nextRows = rows.map((row, currentRowIndex) => {
          if (getRowId(row, rowIdKey) !== rowId) {
            return row;
          }

          const committedRow = {
            ...row,
            [columnKey]: value,
          };

          /**
           * Optional domain adapter normalization.
           *
           * WHY
           *  • The shared spreadsheet engine owns local row state.
           *  • Some consumers need to attach domain identity when a row becomes
           *    meaningful.
           *  • Example: Form Lists assigns optionId to a newly meaningful option
           *    row so dependency editing can safely key by optionId.
           *
           * IMPORTANT
           *  • This callback is generic.
           *  • The canvas does not know about optionId, dropdowns, or Form Lists.
           *  • If the callback returns a non-object, we keep the committed row.
           */
          const normalizedRow =
            typeof normalizeCommittedRow === 'function'
              ? normalizeCommittedRow({
                  row: committedRow,
                  previousRow: row,
                  group,
                  groupId,
                  rowId,
                  rowIndex: Number.isFinite(Number(rowIndex))
                    ? Number(rowIndex)
                    : currentRowIndex,
                  columnKey,
                  value,
                  columns: groupColumns,
                  rowKey,
                  rowIdKey,
                })
              : committedRow;

          const nextRow =
            normalizedRow && typeof normalizedRow === 'object'
              ? normalizedRow
              : committedRow;

          committedRowForEvent = nextRow;

          return nextRow;
        });

        return {
          ...group,
          [rowKey]: padRowsToVisibleCount({
            rows: nextRows,
            columns: groupColumns,
            visibleRowCount: Math.max(
              requestedVisibleRowCount,
              nextRows.length,
            ),
            groupId,
            rowIdKey,
            generatedPrefix: generatedRowPrefix,
          }),
        };
      })
    ));

    updateDirtyState((prev) => markCellDirty(
      prev,
      groupId,
      rowId,
      columnKey,
    ));

    if (typeof onCellCommit === 'function') {
      onCellCommit({
        ...payload,
        rowIndex,
        row: committedRowForEvent || payload?.row || null,
      });
    }

    emitCommitBoundary({
      source: payload?.source || 'cell',
      groupId,
      rowId,
      rowIndex,
      columnKey,
      value,
      row: committedRowForEvent,
    });
  }, [
    updateLocalGroups,
    updateDirtyState,
    onCellCommit,
    emitCommitBoundary,
    normalizeCommittedRow,
    columns,
    rowKey,
    rowIdKey,
    requestedVisibleRowCount,
    generatedRowPrefix,
  ]);

  /**
   * handleClearSelectedCell
   * ---------------------------------------------------------------------------
   * Clears the currently selected cell when the user presses Delete/Backspace
   * while the canvas is in view mode.
   *
   * GOOGLE-SHEETS-LIKE BEHAVIOR
   *  • Single selected editable cell + Delete/Backspace clears the cell.
   *  • The user does not need to double-click into edit mode.
   *
   * ENTERPRISE RULES
   *  • Respect read-only / disabled / custom cells.
   *  • Do not clear while an edit input is active.
   *  • Do not mark an already-empty cell dirty.
   *  • Reuse the same commit path as normal edits so:
   *      - normalizeCommittedRow still runs
   *      - dirty state is marked consistently
   *      - onCellCommit still fires
   *      - onCommitBoundary still fires
   *      - Form Lists / SmartLists adapters stay domain-owned
   */
  const handleClearSelectedCell = useCallback((cellAddress) => {
    if (!cellAddress) return false;

    // If an input/select is actively editing, native Backspace/Delete should
    // edit the input text. Canvas-level clearing only applies in view mode.
    if (editingCellRef.current) {
      return false;
    }

    const descriptor = findCellDescriptor({
      groups: localGroupsRef.current,
      columns,
      cellAddress,
      rowKey,
      rowIdKey,
    });

    if (!descriptor) {
      return false;
    }

    if (!canEditCell({
      descriptor,
      readOnly,
      disabled,
      isCellReadOnly,
      isCellDisabled,
    })) {
      return false;
    }

    const previousValue = descriptor.row?.[descriptor.columnKey];
    const clearedValue = resolveClearedCellValue({
      column: descriptor.column,
      row: descriptor.row,
    });

    if (
      getCellComparableValue(previousValue) ===
      getCellComparableValue(clearedValue)
    ) {
      return false;
    }

    handleCommitCellValue({
      source: 'clear',
      groupId: descriptor.groupId,
      rowId: descriptor.rowId,
      rowIndex: descriptor.rowIndex,
      columnKey: descriptor.columnKey,
      columnIndex: descriptor.columnIndex,
      value: clearedValue,
      previousValue,
      row: descriptor.row,
      column: descriptor.column,
    });

    return true;
  }, [
    columns,
    disabled,
    handleCommitCellValue,
    isCellDisabled,
    isCellReadOnly,
    readOnly,
    rowIdKey,
    rowKey,
  ]);

  const isSortBlockedForColumn = useCallback(({
    group,
    groupId,
    column,
    columnKey,
  } = {}) => {
    if (readOnly || disabled) return true;
    if (group?.readOnly === true || group?.disabled === true) return true;
    if (!isSpreadsheetColumnSortable(column)) return true;

    if (typeof isColumnSortDisabled === 'function') {
      return isColumnSortDisabled({
        group,
        groupId,
        column,
        columnKey,
      }) === true;
    }

    return false;
  }, [
    disabled,
    isColumnSortDisabled,
    readOnly,
  ]);

  /**
   * handleSortColumn
   * ---------------------------------------------------------------------------
   * Sorts one grouped column block locally.
   *
   * ENTERPRISE SORT MODEL
   *  • Sorting is a local spreadsheet operation.
   *  • It reorders whole row objects, preserving rowId, optionId, dependency
   *    metadata, hints, values, and internal row flags.
   *  • It marks the group dirty so adapter-level Save Draft / Publish flushes
   *    the sorted order through the existing dirty-group snapshot path.
   *  • It does NOT write context.
   *  • It does NOT write storage.
   *  • It does NOT write Firestore.
   *
   * GOOGLE-SHEETS-LIKE BEHAVIOR
   *  • First click sorts ascending.
   *  • Second click on the same group/column sorts descending.
   *  • Blank padded rows stay at the bottom.
   *  • Sorting a new column starts ascending.
   */
  const handleSortColumn = useCallback(({
    group,
    groupId,
    column,
    columnKey,
  } = {}) => {
    const safeGroupId = getGroupId(group || { groupId });
    const safeColumnKey = normalizeString(columnKey || getColumnKey(column), '');

    if (!safeGroupId || !safeColumnKey) {
      return;
    }

    if (isSortBlockedForColumn({
      group,
      groupId: safeGroupId,
      column,
      columnKey: safeColumnKey,
    })) {
      return;
    }

    const nextDirection = getNextSortDirection({
      currentSort: sortStateRef.current,
      groupId: safeGroupId,
      columnKey: safeColumnKey,
    });

    let didReorderRows = false;

    updateLocalGroups((prevGroups) => (
      prevGroups.map((currentGroup) => {
        const currentGroupId = getGroupId(currentGroup);

        if (currentGroupId !== safeGroupId) {
          return currentGroup;
        }

        const groupColumns =
          Array.isArray(currentGroup?.columns) &&
          currentGroup.columns.length > 0
            ? currentGroup.columns
            : columns;

        const previousRows = toArraySafe(currentGroup?.[rowKey]);
        const sortedRows = sortSpreadsheetRowsForColumn({
          rows: previousRows,
          columns: groupColumns,
          columnKey: safeColumnKey,
          direction: nextDirection,
        });

        const previousRowOrder = previousRows
          .map((row) => getRowId(row, rowIdKey))
          .join('||');

        const nextRowOrder = sortedRows
          .map((row) => getRowId(row, rowIdKey))
          .join('||');

        didReorderRows = previousRowOrder !== nextRowOrder;

        if (!didReorderRows) {
          return currentGroup;
        }

        return {
          ...currentGroup,
          [rowKey]: padRowsToVisibleCount({
            rows: sortedRows,
            columns: groupColumns,
            visibleRowCount: Math.max(
              requestedVisibleRowCount,
              sortedRows.length,
            ),
            groupId: safeGroupId,
            rowIdKey,
            generatedPrefix: generatedRowPrefix,
          }),
        };
      })
    ));

    updateSortState({
      groupId: safeGroupId,
      columnKey: safeColumnKey,
      direction: nextDirection,
    });

    if (didReorderRows) {
      updateDirtyState((prev) => markGroupDirty(
        prev,
        safeGroupId,
      ));

      emitCommitBoundary({
        source: 'sort',
        groupId: safeGroupId,
        columnKey: safeColumnKey,
        direction: nextDirection,
      });
    }
  }, [
    columns,
    emitCommitBoundary,
    generatedRowPrefix,
    isSortBlockedForColumn,
    requestedVisibleRowCount,
    rowIdKey,
    rowKey,
    updateDirtyState,
    updateLocalGroups,
    updateSortState,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Keyboard navigation                                                      */
  /* ------------------------------------------------------------------------ */

  const navigateFromCell = useCallback((event, cell, {
    scrollIntoView = true,
  } = {}) => {
    if (!cell) return;

    let direction = null;
    let sourceKey = '';

    if (event.key === 'Tab') {
      event.preventDefault();
      direction = event.shiftKey ? 'left' : 'right';
      sourceKey = 'Tab';
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      direction = event.shiftKey ? 'up' : 'down';
      sourceKey = 'Enter';
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      direction = 'right';
      sourceKey = 'ArrowRight';
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      direction = 'left';
      sourceKey = 'ArrowLeft';
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      direction = 'down';
      sourceKey = 'ArrowDown';
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      direction = 'up';
      sourceKey = 'ArrowUp';
    }

    if (!direction) return;

    const isArrowKey =
      sourceKey === 'ArrowRight' ||
      sourceKey === 'ArrowLeft' ||
      sourceKey === 'ArrowDown' ||
      sourceKey === 'ArrowUp';

    /**
     * Spreadsheet navigation semantics
     * -------------------------------------------------------------------------
     * Plain ArrowLeft / ArrowRight should NOT wrap across rows.
     *
     * WHY
     *  • When a user is moving horizontally to the edge of the sheet, the
     *    selector should stop at the first/last visible cell in that visual row.
     *  • Wrapping to the next/previous row makes it easy to overshoot and end
     *    up on the opposite side of the sheet.
     *
     * DATA-ENTRY SEMANTICS
     *  • Tab keeps row-wrapping behavior because it is commonly used for fast
     *    data entry across rows.
     *  • Enter remains vertical and naturally stops at top/bottom.
     *
     * COMMAND / CONTROL EDGE NAVIGATION
     *  • macOS: Command + Arrow
     *  • Windows/Linux: Ctrl + Arrow
     *  • This uses the memoized navigation index, so it does not scan the sheet
     *    on every key press.
     */
    const wrapRows = sourceKey === 'Tab';
    const jumpToEdge = isArrowKey && (event.metaKey || event.ctrlKey);

    const nextCell = resolveNextCellAddress({
      groups: localGroupsRef.current,
      columns,
      currentCell: cell,
      direction,
      rowKey,
      rowIdKey,
      navigationIndex: navigationIndexRef.current,
      wrapRows,
      jumpToEdge,
    });

    if (!nextCell) return;

    updateSelectedCell(nextCell);
    updateEditingCell(null);

    if (scrollIntoView) {
      ensureCellVisible(nextCell);
    }
  }, [
    columns,
    ensureCellVisible,
    rowKey,
    rowIdKey,
    updateEditingCell,
    updateSelectedCell,
  ]);

  const handleCellKeyDown = useCallback((event, cell) => {
    if (!cell) return;

    if (event.defaultPrevented && cell.forceNavigation !== true) {
      return;
    }

    navigateFromCell(event, cell, {
      scrollIntoView: true,
    });
  }, [navigateFromCell]);

  const handleCanvasKeyDown = useCallback((event) => {
    if (event.defaultPrevented) return;
    if (isEditableDomTarget(event.target)) return;

    const currentCell = selectedCellRef.current;

    if (!currentCell) {
      return;
    }

    if (event.key === 'F2') {
      event.preventDefault();
      beginEditingCell(currentCell);
      return;
    }

    /**
     * Delete / Backspace clear selected cell in view mode.
     *
     * WHY
     *  • This matches spreadsheet UX: selected cell + Delete/Backspace clears
     *    cell contents without entering edit mode.
     *  • Native text deletion is still preserved while editing because events
     *    from inputs/selects return early through isEditableDomTarget(...).
     *  • Clearing uses the same commit path as normal edits, so adapters get
     *    consistent dirty/apply/save behavior.
     */
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();

      handleClearSelectedCell(currentCell);

      return;
    }

    if (isPrintableKey(event)) {
      event.preventDefault();

      const nextInitialValue = appendPrintableKeyToTypeahead(
        currentCell,
        event.key,
      );

      beginEditingCell(currentCell, nextInitialValue);
      return;
    }

    navigateFromCell(event, currentCell, {
      scrollIntoView: true,
    });
  }, [
    appendPrintableKeyToTypeahead,
    beginEditingCell,
    handleClearSelectedCell,
    navigateFromCell,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Paste handling                                                           */
  /* ------------------------------------------------------------------------ */

  const handlePaste = useCallback((event, cell) => {
    const text = event.clipboardData?.getData('text/plain') || '';
    const pasteGrid = parseClipboardTextToGrid(text);

    if (!pasteGrid.length || !cell) return;

    event.preventDefault();

    const currentGroups = localGroupsRef.current || [];
    const targetGroupIndex = currentGroups.findIndex(
      (group) => getGroupId(group) === cell.groupId
    );

    if (targetGroupIndex < 0) return;

    const targetGroup = currentGroups[targetGroupIndex];
    const groupId = getGroupId(targetGroup);
    const groupColumns =
      Array.isArray(targetGroup?.columns) &&
      targetGroup.columns.length > 0
        ? targetGroup.columns
        : columns;

    const pasteColumns = getPasteTargetColumns(groupColumns);
    const startPasteColumnIndex = pasteColumns.findIndex(
      (column) => getColumnKey(column) === cell.columnKey
    );

    if (startPasteColumnIndex < 0) return;

    const existingRows = toArraySafe(targetGroup?.[rowKey]);
    const startRowIndex = existingRows.findIndex(
      (row) => getRowId(row, rowIdKey) === cell.rowId
    );

    if (startRowIndex < 0) return;

    const requiredRowCount = startRowIndex + pasteGrid.length;
    const expandedRows = cloneRowsWithCapacity({
      rows: existingRows,
      targetLength: requiredRowCount,
      columns: groupColumns,
      groupId,
      rowIdKey,
      generatedPrefix: generatedRowPrefix,
    });

    let nextDirtyState = dirtyStateRef.current || createEmptyDirtyState();

    const nextRows = expandedRows.map((row) => ({ ...row }));

    pasteGrid.forEach((pasteRow, pasteRowOffset) => {
      const targetRowIndex = startRowIndex + pasteRowOffset;
      const targetRow = nextRows[targetRowIndex];

      if (!targetRow) return;

      const rowId = getRowId(targetRow, rowIdKey);

      pasteRow.forEach((rawValue, pasteColumnOffset) => {
        const targetColumn =
          pasteColumns[startPasteColumnIndex + pasteColumnOffset];

        if (!targetColumn) return;

        const columnKey = getColumnKey(targetColumn);
        if (!columnKey) return;

        const columnIndex = getVisibleColumns(groupColumns).findIndex(
          (column) => getColumnKey(column) === columnKey
        );

        const blocked = isCellBlocked({
          readOnly,
          disabled,
          group: targetGroup,
          row: targetRow,
          column: targetColumn,
          groupId,
          rowId,
          rowIndex: targetRowIndex,
          columnKey,
          columnIndex,
          isCellReadOnly,
          isCellDisabled,
        });

        if (blocked) return;

        const nextValue = normalizePastedValue({
          value: rawValue,
          column: targetColumn,
          row: targetRow,
        });

        const committedRow = {
          ...targetRow,
          [columnKey]: nextValue,
        };

        /**
         * Optional domain adapter normalization for paste.
         *
         * This keeps pasted rows aligned with single-cell commits. For Form
         * Lists, pasted option labels should also receive a stable optionId as
         * soon as the row becomes meaningful.
         */
        const normalizedRow =
          typeof normalizeCommittedRow === 'function'
            ? normalizeCommittedRow({
                row: committedRow,
                previousRow: targetRow,
                group: targetGroup,
                groupId,
                rowId,
                rowIndex: targetRowIndex,
                columnKey,
                value: nextValue,
                columns: groupColumns,
                rowKey,
                rowIdKey,
              })
            : committedRow;

        nextRows[targetRowIndex] =
          normalizedRow && typeof normalizedRow === 'object'
            ? normalizedRow
            : committedRow;

        nextDirtyState = markCellDirty(
          nextDirtyState,
          groupId,
          rowId,
          columnKey,
        );
      });
    });

    const paddedRows = padRowsToVisibleCount({
      rows: nextRows,
      columns: groupColumns,
      visibleRowCount: Math.max(
        requestedVisibleRowCount,
        nextRows.length,
      ),
      groupId,
      rowIdKey,
      generatedPrefix: generatedRowPrefix,
    });

    const nextGroups = currentGroups.map((group, index) => {
      if (index !== targetGroupIndex) return group;

      return {
        ...group,
        [rowKey]: paddedRows,
      };
    });

    updateLocalGroups(nextGroups);
    updateDirtyState(nextDirtyState);

    emitCommitBoundary({
      source: 'paste',
      groupIds: [groupId],
      groupId,
      pastedRowCount: pasteGrid.length,
    });
  }, [
    columns,
    disabled,
    emitCommitBoundary,
    generatedRowPrefix,
    isCellDisabled,
    isCellReadOnly,
    readOnly,
    rowIdKey,
    rowKey,
    updateDirtyState,
    updateLocalGroups,
    normalizeCommittedRow,
    requestedVisibleRowCount,
  ]);

  const handleCanvasPaste = useCallback((event) => {
    if (event.defaultPrevented) return;
    if (isEditableDomTarget(event.target)) return;

    const currentCell = selectedCellRef.current;

    if (!currentCell) {
      return;
    }

    handlePaste(event, currentCell);
  }, [handlePaste]);

  /* ------------------------------------------------------------------------ */
  /* Column resizing / autofit                                                */
  /* ------------------------------------------------------------------------ */

  const handleStartColumnResize = useCallback(({
    event,
    groupId,
    column,
    columnKey,
    startWidth,
  }) => {
    if (!event) return;

    const columnWidthKey = getColumnWidthKey({
      groupId,
      column,
      columnKey,
    });

    if (!columnWidthKey) return;

    event.preventDefault();

    isColumnResizeActiveRef.current = true;
    pendingColumnWidthSyncRef.current = false;

    const startX = event.clientX;
    const minWidth = Number.isFinite(Number(column?.minWidth))
      ? Number(column.minWidth)
      : 40;

    const maxWidth = Number.isFinite(Number(column?.maxWidth))
      ? Number(column.maxWidth)
      : 720;

    const safeStartWidth = clampNumber(
      startWidth,
      minWidth,
      maxWidth,
    );

    /**
     * Establish resize ownership immediately.
     *
     * WHY
     *  • Double-click autofit already creates an explicit scoped width before
     *    the next manual drag, which is why resize works after autofit.
     *  • Normal drag must do the same thing on mouse-down.
     *  • This makes the visual column instance explicit before any mousemove,
     *    so fallback/default width reconciliation cannot make the first drag
     *    snap back.
     */
    columnResizeSessionRef.current = {
      groupId,
      columnKey,
      columnWidthKey,
      startX,
      startWidth: safeStartWidth,
      lastWidth: safeStartWidth,
    };

    logColumnWidthsDebug('resize:start', {
      groupId,
      columnKey,
      columnWidthKey,
      eventType: event?.type,
      eventDetail: event?.detail,
      startX,
      rawStartWidth: startWidth,
      safeStartWidth,
      minWidth,
      maxWidth,
      widthStateAtStart: getColumnWidthDebugState({
        widths: columnWidthsRef.current,
        columnWidthKey,
        columnKey,
      }),
    });

    const applyColumnWidth = (nextWidth, source = 'resize') => {
      if (!isValidColumnResizeWidth(nextWidth)) {
        logColumnWidthsDebug('resize:ignoredInvalidWidth', {
          source,
          groupId,
          columnKey,
          columnWidthKey,
          nextWidth,
          activeResizeSession: columnResizeSessionRef.current,
        });

        return;
      }

      const safeNextWidth = clampNumber(
        Number(nextWidth),
        minWidth,
        maxWidth,
      );

      columnResizeSessionRef.current = {
        ...(columnResizeSessionRef.current || {}),
        groupId,
        columnKey,
        columnWidthKey,
        startX,
        startWidth: safeStartWidth,
        lastWidth: safeNextWidth,
      };

      updateColumnWidths((prev) => {
        if (prev?.[columnWidthKey] === safeNextWidth) {
          return prev;
        }

        logColumnWidthsDebug('resize:applyWidth', {
          source,
          groupId,
          columnKey,
          columnWidthKey,
          previousScopedWidth: prev?.[columnWidthKey],
          previousPlainWidth: prev?.[columnKey],
          nextWidth: safeNextWidth,
          activeResizeSession: columnResizeSessionRef.current,
        });

        return {
          ...prev,
          [columnWidthKey]: safeNextWidth,
        };
      });
    };

    // Commit the scoped visual-column width immediately on mouse-down.
    // This removes the need for the current double-click-before-drag workaround.
    applyColumnWidth(safeStartWidth, 'mousedown');

    const finishColumnResize = () => {
      isColumnResizeActiveRef.current = false;

      if (!pendingColumnWidthSyncRef.current) {
        columnResizeSessionRef.current = null;
        return;
      }

      pendingColumnWidthSyncRef.current = false;

      reconcileColumnWidthsForCurrentGroups({
        force: true,
      });

      columnResizeSessionRef.current = null;
    };

    const flushPendingColumnWidth = () => {
      const pendingWidth = pendingColumnResizeWidthRef.current;

      pendingColumnResizeWidthRef.current = null;
      columnResizeFrameRef.current = null;

      if (!isValidColumnResizeWidth(pendingWidth)) {
        logColumnWidthsDebug('resize:rafSkippedInvalidPendingWidth', {
          pendingWidth,
          activeResizeSession: columnResizeSessionRef.current,
        });

        return;
      }

      applyColumnWidth(pendingWidth, 'raf');
    };

    const handleMouseMove = (moveEvent) => {
      if (typeof moveEvent?.preventDefault === 'function') {
        moveEvent.preventDefault();
      }

      const delta = moveEvent.clientX - startX;
      const nextWidth = clampNumber(
        safeStartWidth + delta,
        minWidth,
        maxWidth,
      );

      pendingColumnResizeWidthRef.current = nextWidth;

      columnResizeSessionRef.current = {
        ...(columnResizeSessionRef.current || {}),
        groupId,
        columnKey,
        columnWidthKey,
        startX,
        startWidth: safeStartWidth,
        lastWidth: nextWidth,
      };

      if (columnResizeFrameRef.current) {
        return;
      }

      columnResizeFrameRef.current = window.requestAnimationFrame(
        flushPendingColumnWidth,
      );
    };

    const handleMouseUp = (upEvent) => {
      if (typeof upEvent?.preventDefault === 'function') {
        upEvent.preventDefault();
      }

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleMouseUp);

      if (columnResizeFrameRef.current) {
        window.cancelAnimationFrame(columnResizeFrameRef.current);
        columnResizeFrameRef.current = null;
      }

      const finalPendingWidth = pendingColumnResizeWidthRef.current;
      const sessionWidth = columnResizeSessionRef.current?.lastWidth;

      pendingColumnResizeWidthRef.current = null;

      const finalWidth = isValidColumnResizeWidth(finalPendingWidth)
        ? finalPendingWidth
        : sessionWidth;

      logColumnWidthsDebug('resize:mouseupFinalWidth', {
        groupId,
        columnKey,
        columnWidthKey,
        finalPendingWidth,
        sessionWidth,
        finalWidth,
        usedPendingWidth: isValidColumnResizeWidth(finalPendingWidth),
        activeResizeSession: columnResizeSessionRef.current,
      });

      if (isValidColumnResizeWidth(finalWidth)) {
        applyColumnWidth(finalWidth, 'mouseup');
      }

      finishColumnResize();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleMouseUp);
  }, [
    reconcileColumnWidthsForCurrentGroups,
    updateColumnWidths,
  ]);

  const handleAutoFitColumn = useCallback(({
    groupId,
    column,
    columnKey,
  }) => {
    const columnWidthKey = getColumnWidthKey({
      groupId,
      column,
      columnKey,
    });

    if (!columnWidthKey) return;

    const visibleGroups = localGroupsRef.current || [];
    const targetGroup = visibleGroups.find(
      (group) => getGroupId(group) === groupId
    );

    if (!targetGroup) return;

    const headerLength = String(column?.label || columnKey || '').length;
    const rows = toArraySafe(targetGroup?.[rowKey]);

    const maxValueLength = rows.reduce((rowMax, row) => {
      const value = row?.[columnKey];
      const length = value == null ? 0 : String(value).length;
      return Math.max(rowMax, length);
    }, headerLength);

    const nextWidth = clampNumber(
      Math.ceil(maxValueLength * 7.5) + 28,
      Number.isFinite(Number(column?.minWidth)) ? Number(column.minWidth) : 40,
      Number.isFinite(Number(column?.maxWidth)) ? Number(column.maxWidth) : 720,
    );

    logColumnWidthsDebug('autofit:computed', {
      groupId,
      columnKey,
      columnWidthKey,
      headerLength,
      maxValueLength,
      nextWidth,
      widthStateBeforeAutofit: getColumnWidthDebugState({
        widths: columnWidthsRef.current,
        columnWidthKey,
        columnKey,
      }),
    });

    updateColumnWidths((prev) => {
      if (prev?.[columnWidthKey] === nextWidth) {
        return prev;
      }

      return {
        ...prev,
        [columnWidthKey]: nextWidth,
      };
    });
  }, [
    rowKey,
    updateColumnWidths,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Row growth                                                               */
  /* ------------------------------------------------------------------------ */

  const appendRowsToGroup = useCallback((groupId, count = rowAppendCount) => {
    const safeCount = Math.max(Number(count) || 0, 0);
    if (!groupId || safeCount <= 0) return;

    updateLocalGroups((prevGroups) => (
      prevGroups.map((group) => {
        if (getGroupId(group) !== groupId) return group;

        const groupColumns =
          Array.isArray(group?.columns) && group.columns.length > 0
            ? group.columns
            : columns;

        const rows = toArraySafe(group?.[rowKey]);
        const nextRows = cloneRowsWithCapacity({
          rows,
          targetLength: rows.length + safeCount,
          columns: groupColumns,
          groupId,
          rowIdKey,
          generatedPrefix: generatedRowPrefix,
        });

        return {
          ...group,
          [rowKey]: padRowsToVisibleCount({
            rows: nextRows,
            columns: groupColumns,
            visibleRowCount: Math.max(
              requestedVisibleRowCount,
              nextRows.length,
            ),
            groupId,
            rowIdKey,
            generatedPrefix: generatedRowPrefix,
          }),
        };
      })
    ));
  }, [
    columns,
    generatedRowPrefix,
    rowAppendCount,
    rowIdKey,
    rowKey,
    requestedVisibleRowCount,
    updateLocalGroups,
  ]);

  /**
   * Footer-driven row growth
   * ---------------------------------------------------------------------------
   * This supports the Google-Sheets-inspired bottom footer:
   *   Add [N] more rows at the bottom
   *
   * We intentionally grow the visible row baseline instead of turning blank
   * rows into persisted data. The actual row data remains local/sparse until
   * the user edits or pastes into those rows.
   * ---------------------------------------------------------------------------
   */
  const handleAddVisibleRows = useCallback(() => {
    const parsedCount = Number(rowsToAddInput);
    const safeCount =
      Number.isFinite(parsedCount) && parsedCount > 0
        ? Math.floor(parsedCount)
        : rowAppendCount;

    setRequestedVisibleRowCount((prev) => {
      const next = prev + safeCount;

      console.log('[GroupedSpreadsheetCanvas] Added more visible rows:', {
        addedCount: safeCount,
        previousRequestedVisibleRowCount: prev,
        nextRequestedVisibleRowCount: next,
      });

      return next;
    });
  }, [
    rowAppendCount,
    rowsToAddInput,
  ]);

  const handleRowsToAddInputChange = useCallback((event) => {
    const raw = event.target.value;
    const numericOnly = String(raw || '').replace(/[^\d]/g, '');

    setRowsToAddInput(numericOnly);
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Snapshots / imperative API                                               */
  /* ------------------------------------------------------------------------ */

  /**
   * buildWorkspaceSnapshotForGroups
   * ---------------------------------------------------------------------------
   * Builds an outgoing snapshot for the provided group subset.
   *
   * WHY THIS EXISTS
   *  • getWorkspaceSnapshot(...) must continue supporting full-workspace reads.
   *  • getDirtyWorkspaceSnapshot(...) needs the same outgoing row cleanup but
   *    only for dirty groups.
   *  • Keeping the common snapshot mapping here prevents the shared Spreadsheet
   *    engine from growing two divergent snapshot code paths.
   *
   * IMPORTANT
   *  • This remains domain-free.
   *  • It does not know about Form Lists, SmartLists, or persistence.
   *  • It does not mutate localGroups.
   *  • It preserves buildGroupRowsSnapshot(...) behavior, including dropping
   *    blank padded rows unless includeBlankRows is true.
   */
  const buildWorkspaceSnapshotForGroups = useCallback(({
    sourceGroups = [],
    includeBlankRows = false,
    snapshotMode = 'all',
    sourceGroupCount = null,
    requestedGroupIds = null,
  } = {}) => {
    const safeSourceGroups = Array.isArray(sourceGroups)
      ? sourceGroups
      : [];

    return {
      groups: safeSourceGroups.map((group) => {
        const groupColumns =
          Array.isArray(group?.columns) && group.columns.length > 0
            ? group.columns
            : columns;

        return {
          ...group,
          [rowKey]: buildGroupRowsSnapshot({
            group,
            columns: groupColumns,
            rowKey,
            includeBlankRows,
          }),
        };
      }),
      dirtyState: dirtyStateRef.current,
      columnWidths: columnWidthsRef.current,
      selectedCell: selectedCellRef.current,

      /**
       * Snapshot metadata
       * -----------------------------------------------------------------------
       * Consumers can inspect this for diagnostics, but existing consumers that
       * only read `groups`, `dirtyState`, `columnWidths`, or `selectedCell`
       * continue working unchanged.
       */
      snapshotMode,
      sourceGroupCount:
        Number.isFinite(Number(sourceGroupCount))
          ? Number(sourceGroupCount)
          : safeSourceGroups.length,
      requestedGroupIds: Array.isArray(requestedGroupIds)
        ? requestedGroupIds
        : null,
    };
  }, [
    columns,
    rowKey,
  ]);

  const getWorkspaceSnapshot = useCallback(({
    includeBlankRows = false,
  } = {}) => (
    buildWorkspaceSnapshotForGroups({
      sourceGroups: localGroupsRef.current || [],
      includeBlankRows,
      snapshotMode: 'all',
      sourceGroupCount: Array.isArray(localGroupsRef.current)
        ? localGroupsRef.current.length
        : 0,
      requestedGroupIds: null,
    })
  ), [
    buildWorkspaceSnapshotForGroups,
  ]);

  /**
   * getDirtyWorkspaceSnapshot
   * ---------------------------------------------------------------------------
   * Returns an outgoing snapshot for dirty groups only.
   *
   * PURPOSE
   *  • Let adapter layers build sparse apply plans without first scanning every
   *    group in the sheet.
   *  • Keep Save Draft / Publish flushes deterministic and fast on large forms.
   *  • Preserve the existing full getWorkspaceSnapshot(...) API for consumers
   *    that intentionally need a complete workspace snapshot.
   *
   * ENTERPRISE RULE
   *  • Dirty-state ownership stays inside the shared Spreadsheet engine.
   *  • Domain-specific apply logic stays outside the shared engine.
   *  • This method only selects dirty groups and builds a normal outgoing
   *    snapshot for that subset.
   */
  const getDirtyWorkspaceSnapshot = useCallback(({
    includeBlankRows = false,
    dirtyGroupIds = null,
  } = {}) => {
    const localGroupsForSnapshot = localGroupsRef.current || [];
    const explicitDirtyGroupIds =
      dirtyGroupIds instanceof Set
        ? Array.from(dirtyGroupIds)
        : Array.isArray(dirtyGroupIds)
          ? dirtyGroupIds
          : getDirtyGroupIdsArray(dirtyStateRef.current);

    const dirtyGroupIdSet = new Set(
      explicitDirtyGroupIds
        .map((groupId) => normalizeString(groupId, ''))
        .filter(Boolean),
    );

    if (dirtyGroupIdSet.size === 0) {
      return buildWorkspaceSnapshotForGroups({
        sourceGroups: [],
        includeBlankRows,
        snapshotMode: 'dirty',
        sourceGroupCount: Array.isArray(localGroupsForSnapshot)
          ? localGroupsForSnapshot.length
          : 0,
        requestedGroupIds: [],
      });
    }

    const dirtyGroups = localGroupsForSnapshot.filter((group) => {
      const groupId = getGroupId(group);
      return groupId && dirtyGroupIdSet.has(groupId);
    });

    return buildWorkspaceSnapshotForGroups({
      sourceGroups: dirtyGroups,
      includeBlankRows,
      snapshotMode: 'dirty',
      sourceGroupCount: Array.isArray(localGroupsForSnapshot)
        ? localGroupsForSnapshot.length
        : 0,
      requestedGroupIds: Array.from(dirtyGroupIdSet),
    });
  }, [
    buildWorkspaceSnapshotForGroups,
  ]);

  /**
   * flushPendingEdit
   * ---------------------------------------------------------------------------
   * Commits the active edit cell, if one exists, before an external adapter
   * reads a workspace snapshot.
   *
   * WHY
   *  • Save Draft / Publish / route-exit boundaries need deterministic data.
   *  • The active edit input may not have blurred yet.
   *  • This keeps the canvas domain-free: it only commits the current cell into
   *    localGroups and dirtyState; it does not know what Form Lists will do
   *    with the resulting dirty snapshot.
   */
  const flushPendingEdit = useCallback(({
    reason = 'external_flush',
  } = {}) => {
    const activeEditingCell = editingCellRef.current;

    if (!activeEditingCell) {
      return {
        status: 'skipped',
        reason: 'no_active_editing_cell',
      };
    }

    const key = makeCellRefKey({
      groupId: activeEditingCell.groupId,
      rowId: activeEditingCell.rowId,
      columnKey: activeEditingCell.columnKey,
    });

    const refRecord = cellRefsRef.current.get(key);

    if (
      !refRecord ||
      typeof refRecord.flushPendingEdit !== 'function'
    ) {
      return {
        status: 'skipped',
        reason: 'missing_cell_flush_handler',
      };
    }

    const result = refRecord.flushPendingEdit({
      reason,
      cellAddress: activeEditingCell,
    });

    updateEditingCell(null);

    return result || {
      status: 'success',
      reason,
    };
  }, [
    updateEditingCell,
  ]);

  useImperativeHandle(ref, () => ({
    getWorkspaceSnapshot,
    getDirtyWorkspaceSnapshot,
    flushPendingEdit,
    getDirtyState: () => dirtyStateRef.current,
    getDirtyGroupIds: () => getDirtyGroupIdsArray(dirtyStateRef.current),
    getLocalGroups: () => localGroupsRef.current,
    getColumnWidths: () => columnWidthsRef.current,
    getSelectedCell: () => selectedCellRef.current,
    clearDirtyState: () => updateDirtyState(clearDirtyState()),
    resetFromGroups: (nextGroups = []) => {
      const normalized = normalizeGroupsForCanvas({
        groups: nextGroups,
        columns,
        groupIdKey,
        rowIdKey,
        rowKey,
        visibleRowCount: requestedVisibleRowCount,
        generatedPrefix: generatedRowPrefix,
      });

      updateLocalGroups(normalized);
      updateDirtyState(clearDirtyState());
      updateEditingCell(null);
      updateSortState(null);
    },
    appendRowsToGroup,
    focusCanvas,
  }), [
    appendRowsToGroup,
    columns,
    flushPendingEdit,
    focusCanvas,
    generatedRowPrefix,
    getDirtyWorkspaceSnapshot,
    getWorkspaceSnapshot,
    groupIdKey,
    rowIdKey,
    rowKey,
    updateDirtyState,
    updateEditingCell,
    updateLocalGroups,
    updateSortState,
    requestedVisibleRowCount,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Render styles                                                            */
  /* ------------------------------------------------------------------------ */

  const totalCanvasContentWidth = useMemo(() => {
    return Math.max(
      getCanvasContentWidth({
        groups: localGroups,
        columns,
        columnWidths,
        rowNumberWidth,
      }),
      1,
    );
  }, [
    localGroups,
    columns,
    columnWidths,
    rowNumberWidth,
  ]);

  const canvasSx = useMemo(() => ({
    width: '100%',
    height: '100%',
    overflow: 'auto',

    /**
     * Frozen-pane-aware programmatic scrolling
     * -------------------------------------------------------------------------
     * Keyboard navigation uses element.scrollIntoView(...) to keep the selected
     * cell visible.
     *
     * WHY
     *  • The row-number rail is position: sticky; left: 0.
     *  • The group header and column header rows are also sticky.
     *  • Native scrollIntoView does not automatically know that those sticky
     *    panes cover part of the scroll viewport.
     *
     * GOOGLE-SHEETS-LIKE RULE
     *  • A selected cell should scroll into the visible body region beside the
     *    frozen row-number rail, not underneath it.
     *  • A selected cell should scroll below the frozen header rows, not
     *    underneath them.
     *
     * ENTERPRISE NOTE
     *  • scrollPadding only affects programmatic scroll positioning.
     *  • It does not change manual scrolling, layout width calculations,
     *    row rendering, column resizing, or cell editing.
     */
    scrollPaddingLeft: `${rowNumberWidth}px`,
    scrollPaddingTop: `${groupHeaderHeight + columnHeaderHeight}px`,

    backgroundColor: 'background.default',
    border: '1px solid',
    borderColor: 'divider',
    boxSizing: 'border-box',
    overscrollBehavior: 'contain',
    outline: 'none',
    ...(typeof sx === 'object' && !Array.isArray(sx) ? sx : {}),
  }), [
    sx,
    rowNumberWidth,
    groupHeaderHeight,
    columnHeaderHeight,
  ]);

  const contentSx = useMemo(() => ({
    minWidth: '100%',
    width: 'fit-content',
  }), []);

  const innerSx = useMemo(() => ({
    display: 'block',
    minWidth: '100%',
    width: 'fit-content',
  }), []);

  const footerSx = useMemo(() => ({
    display: 'flex',
    alignItems: 'stretch',
    minHeight: `${GOOGLE_SHEETS_FOOTER_HEIGHT}px`,
    height: `${GOOGLE_SHEETS_FOOTER_HEIGHT}px`,
    width: totalCanvasContentWidth,
    minWidth: '100%',
    backgroundColor: GOOGLE_SHEETS_FOOTER_BG,
    borderTop: `1px solid ${GOOGLE_SHEETS_FOOTER_BORDER}`,
    boxSizing: 'border-box',
  }), [totalCanvasContentWidth]);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <Box
      ref={scrollContainerRef}
      tabIndex={0}
      role="region"
      aria-label="Grouped spreadsheet canvas"
      onKeyDown={handleCanvasKeyDown}
      onPaste={handleCanvasPaste}
      onWheel={handleManualScrollIntent}
      onTouchMove={handleManualScrollIntent}
      sx={canvasSx}
    >
      <Box sx={contentSx}>
        <Box sx={innerSx}>
          <GroupedSpreadsheetSheet
            groups={localGroups}
            columns={columns}
            rowCount={requestedVisibleRowCount}
            rowKey={rowKey}
            columnWidths={columnWidths}
            selectedCell={selectedCell}
            editingCell={editingCell}
            dirtyCellsByGroup={dirtyState?.dirtyCellsByGroup || {}}
            showDirtyCellIndicator={showDirtyCellIndicator}
            sortState={sortState}
            onSortColumn={handleSortColumn}
            isColumnSortDisabled={isSortBlockedForColumn}
            rowHeight={rowHeight}
            rowNumberWidth={rowNumberWidth}
            groupHeaderHeight={groupHeaderHeight}
            columnHeaderHeight={columnHeaderHeight}
            rowIdKey={rowIdKey}
            readOnly={readOnly}
            disabled={disabled}
            registerCellRef={registerCellRef}
            onSelectCell={handleSelectCell}
            onCommitCellValue={handleCommitCellValue}
            onRequestCellEdit={handleRequestCellEdit}
            onEndCellEdit={handleEndCellEdit}
            onEditInputReady={handleEditInputReady}
            onKeyDown={handleCellKeyDown}
            onPaste={handlePaste}
            renderCustomCell={renderCustomCell}
            renderGroupHeaderContent={renderGroupHeaderContent}
            renderGroupActions={renderGroupActions}
            isCellReadOnly={isCellReadOnly}
            isCellDisabled={isCellDisabled}
            isCellDirty={isCellDirty}
            onStartColumnResize={handleStartColumnResize}
            onAutoFitColumn={handleAutoFitColumn}
          />
        </Box>

        {/* -------------------------------------------------------------------
         * Google-Sheets-inspired bottom row growth footer
         * -------------------------------------------------------------------
         * UX GOALS
         *  • visually separate the control from the grid body with a light
         *    gray bar
         *  • keep the left row-number lane visually continuous
         *  • present:
         *      Add [outlined numeric field] more rows at the bottom
         *  • make the Add affordance feel lightweight by default, but stronger
         *    on hover/focus like Google Sheets
         *
         * IMPORTANT
         *  • This footer only captures the requested count and updates the
         *    shared canvas visible row baseline.
         *  • Blank padded rows are still dropped from outgoing snapshots.
         * ----------------------------------------------------------------- */}
        <Box sx={footerSx}>
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 5,
              width: `${rowNumberWidth}px`,
              minWidth: `${rowNumberWidth}px`,
              height: `${GOOGLE_SHEETS_FOOTER_HEIGHT}px`,
              borderRight: `1px solid ${GOOGLE_SHEETS_FOOTER_BORDER}`,
              backgroundColor: GOOGLE_SHEETS_FOOTER_BG,
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              color: GOOGLE_SHEETS_TEXT,
            }}
          >
            <Button
              onClick={handleAddVisibleRows}
              onMouseEnter={() => setIsAddHovered(true)}
              onMouseLeave={() => setIsAddHovered(false)}
              variant="text"
              size="small"
              disableElevation
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0.25,
                lineHeight: 1.2,
                fontSize: '0.8125rem',
                fontWeight: 700,
                textTransform: 'none',
                color: GOOGLE_SHEETS_ADD_BLUE,
                borderRadius: '4px',
                backgroundColor: isAddHovered
                  ? 'rgba(26, 115, 232, 0.08)'
                  : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(26, 115, 232, 0.08)',
                },
                '&:focus-visible': {
                  backgroundColor: 'rgba(26, 115, 232, 0.12)',
                },
              }}
            >
              Add
            </Button>

            <TextField
              value={rowsToAddInput}
              onChange={handleRowsToAddInputChange}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddVisibleRows();
                }
              }}
              variant="outlined"
              size="small"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                'aria-label': 'Number of rows to add',
                style: {
                  textAlign: 'center',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                },
              }}
              sx={{
                width: '88px',
                minWidth: '88px',
                '& .MuiOutlinedInput-root': {
                  height: '30px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  '& fieldset': {
                    borderColor: '#c4c7c5',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9aa0a6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: GOOGLE_SHEETS_ADD_BLUE,
                    borderWidth: '1px',
                  },
                },
              }}
            />

            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8125rem',
                color: GOOGLE_SHEETS_TEXT,
                whiteSpace: 'nowrap',
              }}
            >
              more rows at the bottom
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}));

GroupedSpreadsheetCanvas.displayName = 'GroupedSpreadsheetCanvas';

GroupedSpreadsheetCanvas.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object),
  initialColumnWidths: PropTypes.object,
  visibleRowCount: PropTypes.number,
  rowAppendCount: PropTypes.number,
  rowHeight: PropTypes.number,
  rowNumberWidth: PropTypes.number,
  groupHeaderHeight: PropTypes.number,
  columnHeaderHeight: PropTypes.number,
  groupIdKey: PropTypes.string,
  rowIdKey: PropTypes.string,
  rowKey: PropTypes.string,
  generatedRowPrefix: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
  onDirtyStateChange: PropTypes.func,
  includeDirtyDiagnostics: PropTypes.bool,
  showDirtyCellIndicator: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onColumnWidthsChange: PropTypes.func,
  onCellCommit: PropTypes.func,
  onCommitBoundary: PropTypes.func,
  normalizeCommittedRow: PropTypes.func,
  renderCustomCell: PropTypes.func,
  renderGroupHeaderContent: PropTypes.func,
  renderGroupActions: PropTypes.func,
  isCellReadOnly: PropTypes.func,
  isCellDisabled: PropTypes.func,
  isCellDirty: PropTypes.func,
  isColumnSortDisabled: PropTypes.func,
};

export default GroupedSpreadsheetCanvas;